import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Upload as UploadIcon,
    Download as DownloadIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    AccountBalance as BudgetIcon
} from '@mui/icons-material';
import apiService from '../services/api.service';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import * as XLSX from 'xlsx';

interface MonthlyBudget {
    budgetid: number;
    projectcode: string;
    projectname: string;
    departmentcode: string;
    departmentname: string;
    costcode: string;
    costcodename: string;
    fiscalyear: number;
    month: number;
    valuesar: number;
    tolerancepercent: number;
    allocatedytd: number;
    consumedytd: number;
    availableytd: number;
    availablewithtolerance: number;
}

interface Project {
    code: string;
    name: string;
}

interface Department {
    code: string;
    name: string;
}

interface CostCode {
    code: string;
    name: string;
}

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const BudgetNew: React.FC = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [budgets, setBudgets] = useState<MonthlyBudget[]>([]);
    const [filteredBudgets, setFilteredBudgets] = useState<MonthlyBudget[]>([]);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
    const [costCodes, setCostCodes] = useState<CostCode[]>([]);
    const [selectedCostCode, setSelectedCostCode] = useState<CostCode | null>(null);

    // Filters
    const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
    const [monthFilter, setMonthFilter] = useState<number>(0); // 0 = All
    const [searchText, setSearchText] = useState('');

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBudget, setEditingBudget] = useState<MonthlyBudget | null>(null);
    const [formData, setFormData] = useState({
        projectCode: '',
        projectName: '',
        departmentCode: '',
        departmentName: '',
        costCode: '',
        costCodeName: '',
        fiscalYear: new Date().getFullYear(),
        month: 1,
        valueSAR: 0,
        tolerancePercent: 5.00
    });

    const loadBudgets = useCallback(async () => {
        setLoading(true);
        try {
            const selectedCompanyId = localStorage.getItem('selectedCompanyId');
            const params: any = { year: yearFilter };
            if (monthFilter > 0) params.month = monthFilter;
            if (selectedCompanyId) params.companyId = Number(selectedCompanyId);

            const response = await apiService.getMonthlyBudgets(params);
            const data = response?.data || [];
            setBudgets(data);
            applyFilters(data, searchText);
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to load budgets',
                severity: 'error'
            }));
        } finally {
            setLoading(false);
        }
    }, [yearFilter, monthFilter, searchText, dispatch]);

    useEffect(() => {
        loadBudgets();
        loadProjects();
        loadDepartments();
        loadCostCodes();
    }, [loadBudgets]);

    const loadProjects = async () => {
        try {
            const selectedCompanyId = localStorage.getItem('selectedCompanyId');
            const params = selectedCompanyId ? { companyId: Number(selectedCompanyId) } : undefined;
            const response = await apiService.getBudgetProjects(params);
            setProjects(response?.data || []);
        } catch (error: any) {
            console.error('Failed to load projects:', error);
        }
    };

    const loadDepartments = async () => {
        try {
            const selectedCompanyId = localStorage.getItem('selectedCompanyId');
            const params = selectedCompanyId ? { companyId: Number(selectedCompanyId) } : undefined;
            const response = await apiService.getBudgetDepartments(params);
            setDepartments(response?.data || []);
        } catch (error: any) {
            console.error('Failed to load departments:', error);
        }
    };

    const loadCostCodes = async () => {
        try {
            const selectedCompanyId = localStorage.getItem('selectedCompanyId');
            const params = selectedCompanyId ? { companyId: Number(selectedCompanyId) } : undefined;
            const response = await apiService.getBudgetCostCodes(params);
            setCostCodes(response?.data || []);
        } catch (error: any) {
            console.error('Failed to load cost codes:', error);
        }
    };

    const applyFilters = (data: MonthlyBudget[], search: string) => {
        let filtered = [...data];

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(b =>
                b.projectcode?.toLowerCase().includes(searchLower) ||
                b.projectname?.toLowerCase().includes(searchLower) ||
                b.departmentcode?.toLowerCase().includes(searchLower) ||
                b.departmentname?.toLowerCase().includes(searchLower) ||
                b.costcode?.toLowerCase().includes(searchLower) ||
                b.costcodename?.toLowerCase().includes(searchLower)
            );
        }

        setFilteredBudgets(filtered);
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        applyFilters(budgets, value);
    };

    const handleOpenDialog = (budget?: MonthlyBudget) => {
        if (budget) {
            setEditingBudget(budget);
            setFormData({
                projectCode: budget.projectcode || '',
                projectName: budget.projectname || '',
                departmentCode: budget.departmentcode || '',
                departmentName: budget.departmentname || '',
                costCode: budget.costcode || '',
                costCodeName: budget.costcodename || '',
                fiscalYear: budget.fiscalyear,
                month: budget.month,
                valueSAR: budget.valuesar,
                tolerancePercent: budget.tolerancepercent || 5.00
            });
            // Find and set the selected project
            const project = projects.find(p => p.code === budget.projectcode);
            setSelectedProject(project || null);
            // Find and set the selected department
            const department = departments.find(d => d.code === budget.departmentcode);
            setSelectedDepartment(department || null);
            // Find and set the selected cost code
            const costCode = costCodes.find(c => c.code === budget.costcode);
            setSelectedCostCode(costCode || null);
        } else {
            setEditingBudget(null);
            setFormData({
                projectCode: '',
                projectName: '',
                departmentCode: '',
                departmentName: '',
                costCode: '',
                costCodeName: '',
                fiscalYear: yearFilter,
                month: 1,
                valueSAR: 0,
                tolerancePercent: 5.00
            });
            setSelectedProject(null);
            setSelectedDepartment(null);
            setSelectedCostCode(null);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingBudget(null);
    };

    const handleSave = async () => {
        try {
            if (!formData.projectCode || !formData.fiscalYear || !formData.month) {
                dispatch(showSnackbar({
                    message: 'Project Code, Year, and Month are required',
                    severity: 'error'
                }));
                return;
            }

            const selectedCompanyId = localStorage.getItem('selectedCompanyId');
            const budgetData = {
                companyId: selectedCompanyId ? Number(selectedCompanyId) : null,
                projectCode: formData.projectCode,
                projectName: formData.projectName,
                departmentCode: formData.departmentCode,
                departmentName: formData.departmentName,
                costCode: formData.costCode,
                costCodeName: formData.costCodeName,
                fiscalYear: formData.fiscalYear,
                month: formData.month,
                valueSAR: formData.valueSAR,
                tolerancePercent: formData.tolerancePercent
            };

            if (editingBudget) {
                await apiService.updateMonthlyBudget(editingBudget.budgetid, budgetData);
                dispatch(showSnackbar({
                    message: 'Budget updated successfully',
                    severity: 'success'
                }));
            } else {
                await apiService.createMonthlyBudget(budgetData);
                dispatch(showSnackbar({
                    message: 'Budget created successfully',
                    severity: 'success'
                }));
            }

            handleCloseDialog();

            // If the created/updated budget's year doesn't match current filter, update the filter
            // The useEffect will automatically reload budgets when filters change
            if (formData.fiscalYear !== yearFilter) {
                setYearFilter(formData.fiscalYear);
            } else if (monthFilter > 0 && formData.month !== monthFilter) {
                // If year matches but month doesn't, update month filter
                setMonthFilter(formData.month);
            } else {
                // If filters match, manually reload budgets
                loadBudgets();
            }
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to save budget',
                severity: 'error'
            }));
        }
    };

    const handleDelete = async () => {
        if (selectedRows.length === 0) {
            dispatch(showSnackbar({
                message: 'Please select budgets to delete',
                severity: 'warning'
            }));
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} budget(s)?`)) {
            return;
        }

        try {
            await apiService.bulkDeleteMonthlyBudgets(selectedRows as number[]);
            dispatch(showSnackbar({
                message: `${selectedRows.length} budget(s) deleted successfully`,
                severity: 'success'
            }));
            setSelectedRows([]);
            loadBudgets();
        } catch (error: any) {
            dispatch(showSnackbar({
                message: error.message || 'Failed to delete budgets',
                severity: 'error'
            }));
        }
    };

    const handleDownloadTemplate = () => {
        const templateData = [
            {
                'Project': 'PRJ001',
                'Project Name': 'Sample Project',
                'Department': 'DEPT01',
                'Department Name': 'Engineering',
                'Project Cost Code': 'CC001',
                'Project Cost Name': 'Labor',
                'Year': new Date().getFullYear(),
                'Month': 1,
                'Value in SAR': 50000.00,
                'Tolerance %': 5.00
            },
            {
                'Project': 'PRJ002',
                'Project Name': 'Another Project',
                'Department': 'DEPT02',
                'Department Name': 'Marketing',
                'Project Cost Code': 'CC002',
                'Project Cost Name': 'Materials',
                'Year': new Date().getFullYear(),
                'Month': 2,
                'Value in SAR': 75000.00,
                'Tolerance %': 5.00
            }
        ];

        const ws = XLSX.utils.json_to_sheet(templateData);

        // Set column widths for better visibility
        ws['!cols'] = [
            { wch: 15 },  // Project
            { wch: 25 },  // Project Name
            { wch: 15 },  // Department
            { wch: 25 },  // Department Name
            { wch: 20 },  // Project Cost Code
            { wch: 25 },  // Project Cost Name
            { wch: 10 },  // Year
            { wch: 10 },  // Month
            { wch: 18 },  // Value in SAR
            { wch: 12 }   // Tolerance %
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Budget Template');
        XLSX.writeFile(wb, 'Monthly_Budget_Template.xlsx');

        dispatch(showSnackbar({
            message: 'Template downloaded successfully',
            severity: 'success'
        }));
    };

    const handleExport = () => {
        const exportData = filteredBudgets.map((b, index) => ({
            '#': index + 1,
            'Project': b.projectcode,
            'Project Name': b.projectname,
            'Department': b.departmentcode,
            'Department Name': b.departmentname,
            'Project Cost Code': b.costcode,
            'Project Cost Name': b.costcodename,
            'Year': b.fiscalyear,
            'Month': b.month,
            'Value in SAR': b.valuesar
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Budgets');
        XLSX.writeFile(wb, `Monthly_Budget_${yearFilter}.xlsx`);

        dispatch(showSnackbar({
            message: 'Budget exported successfully',
            severity: 'success'
        }));
    };

    const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];

                const selectedCompanyId = localStorage.getItem('selectedCompanyId');
                const budgetsToImport = jsonData.map((row: any) => ({
                    companyId: selectedCompanyId ? Number(selectedCompanyId) : null,
                    projectCode: String(row['Project'] || row['ProjectCode'] || '').trim(),
                    projectName: String(row['Project Name'] || row['ProjectName'] || '').trim(),
                    departmentCode: String(row['Department'] || row['DepartmentCode'] || '').trim(),
                    departmentName: String(row['Department Name'] || row['DepartmentName'] || '').trim(),
                    costCode: String(row['Project Cost Code'] || row['CostCode'] || '').trim(),
                    costCodeName: String(row['Project Cost Name'] || row['CostCodeName'] || '').trim(),
                    fiscalYear: Number(row['Year'] || row['FiscalYear'] || yearFilter),
                    month: Number(row['Month'] || 1),
                    valueSAR: Number(row['Value in SAR'] || row['ValueSAR'] || 0),
                    tolerancePercent: Number(row['Tolerance %'] || row['TolerancePercent'] || 5.00)
                })).filter(b => b.projectCode); // Filter out empty rows

                if (budgetsToImport.length === 0) {
                    dispatch(showSnackbar({
                        message: 'No valid budget data found in file',
                        severity: 'warning'
                    }));
                    return;
                }

                await apiService.bulkUpsertMonthlyBudgets(budgetsToImport);
                dispatch(showSnackbar({
                    message: `${budgetsToImport.length} budget(s) imported successfully`,
                    severity: 'success'
                }));
                loadBudgets();
            } catch (error: any) {
                dispatch(showSnackbar({
                    message: error.message || 'Failed to import budgets',
                    severity: 'error'
                }));
            }
        };
        reader.readAsArrayBuffer(file);
        event.target.value = ''; // Reset input
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: '#',
            width: 60,
            renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.row.budgetid) + 1
        },
        {
            field: 'projectcode',
            headerName: 'Project',
            width: 120,
            editable: false
        },
        {
            field: 'projectname',
            headerName: 'Project Name',
            width: 200,
            flex: 1
        },
        {
            field: 'departmentcode',
            headerName: 'Department',
            width: 120
        },
        {
            field: 'departmentname',
            headerName: 'Department Name',
            width: 180
        },
        {
            field: 'costcode',
            headerName: 'Project Cost Code',
            width: 140
        },
        {
            field: 'costcodename',
            headerName: 'Project Cost Name',
            width: 180
        },
        {
            field: 'fiscalyear',
            headerName: 'Year',
            width: 80,
            align: 'center'
        },
        {
            field: 'month',
            headerName: 'Month',
            width: 80,
            align: 'center',
            renderCell: (params) => monthNames[params.value - 1] || params.value
        },
        {
            field: 'valuesar',
            headerName: 'Budget SAR',
            width: 120,
            align: 'right',
            renderCell: (params) => params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        {
            field: 'allocatedytd',
            headerName: 'Allocated YTD',
            width: 130,
            align: 'right',
            renderCell: (params) => params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        {
            field: 'consumedytd',
            headerName: 'Consumed YTD',
            width: 130,
            align: 'right',
            renderCell: (params) => params.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        {
            field: 'availableytd',
            headerName: 'Available YTD',
            width: 130,
            align: 'right',
            renderCell: (params) => {
                const value = params.value || 0;
                const color = value < 0 ? '#d32f2f' : value > 0 ? '#2e7d32' : '#666';
                return (
                    <span style={{ color, fontWeight: 500 }}>
                        {value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                );
            }
        },
        {
            field: 'availablewithtolerance',
            headerName: 'Available w/Tol',
            width: 130,
            align: 'right',
            renderCell: (params) => {
                const value = params.value || 0;
                const color = value < 0 ? '#d32f2f' : value > 0 ? '#2e7d32' : '#666';
                return (
                    <span style={{ color, fontWeight: 500 }}>
                        {value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                );
            }
        },
        {
            field: 'tolerancepercent',
            headerName: 'Tol %',
            width: 70,
            align: 'center',
            renderCell: (params) => `${params.value || 5}%`
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 80,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(params.row)}
                    color="primary"
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <Box component="section" aria-labelledby="budget-heading" sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                    mb={3}
                >
                    <Typography
                        id="budget-heading"
                        variant="h5"
                        component="h1"
                        sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
                    >
                        <BudgetIcon sx={{ mr: 1, verticalAlign: 'middle' }} aria-hidden="true" />
                        Monthly Budget Management
                    </Typography>
                </Stack>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Manage monthly budgets by Project, Department, and Cost Code. Use bulk import for adding multiple budgets at once.
                </Alert>

                {/* Filters and Actions */}
                <Stack spacing={2} mb={3}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={yearFilter}
                                label="Year"
                                onChange={(e) => setYearFilter(Number(e.target.value))}
                                size="small"
                            >
                                {[2024, 2025, 2026, 2027, 2028].map(year => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Month</InputLabel>
                            <Select
                                value={monthFilter}
                                label="Month"
                                onChange={(e) => setMonthFilter(Number(e.target.value))}
                                size="small"
                            >
                                <MenuItem value={0}>All Months</MenuItem>
                                {monthNames.map((name, index) => (
                                    <MenuItem key={index + 1} value={index + 1}>{name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            size="small"
                            sx={{ minWidth: 250 }}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />

                        <Box sx={{ flex: 1 }} />

                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadBudgets}
                            disabled={loading}
                        >
                            Refresh
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadTemplate}
                        >
                            Template
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleExport}
                            disabled={filteredBudgets.length === 0}
                        >
                            Export
                        </Button>

                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                        >
                            Import
                            <input
                                type="file"
                                hidden
                                accept=".xlsx,.xls,.csv"
                                onChange={handleImport}
                            />
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleDelete}
                            disabled={selectedRows.length === 0}
                        >
                            Delete ({selectedRows.length})
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                        >
                            Add Budget
                        </Button>
                    </Stack>

                    {selectedRows.length > 0 && (
                        <Chip
                            label={`${selectedRows.length} budget(s) selected`}
                            onDelete={() => setSelectedRows([])}
                            color="primary"
                        />
                    )}
                </Stack>

                {/* DataGrid */}
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={filteredBudgets}
                        columns={columns}
                        getRowId={(row) => row.budgetid}
                        checkboxSelection
                        disableRowSelectionOnClick
                        onRowSelectionModelChange={setSelectedRows}
                        rowSelectionModel={selectedRows}
                        autoHeight
                        rowHeight={32}
                        columnHeaderHeight={36}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 }
                            }
                        }}
                        pageSizeOptions={[25, 50, 100]}
                        sx={{
                            '& .MuiDataGrid-cell': {
                                borderRight: '1px solid #e0e0e0',
                                padding: '0 8px',
                                fontSize: '0.875rem',
                                lineHeight: '32px'
                            },
                            '& .MuiDataGrid-columnHeader': {
                                padding: '0 8px',
                                fontSize: '0.875rem',
                                fontWeight: 600
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600
                            }
                        }}
                    />
                )}
            </Paper>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingBudget ? 'Edit Budget' : 'Add New Budget'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Autocomplete
                            options={projects}
                            value={selectedProject}
                            onChange={(_event, newValue) => {
                                setSelectedProject(newValue);
                                if (newValue) {
                                    setFormData({
                                        ...formData,
                                        projectCode: newValue.code,
                                        projectName: newValue.name
                                    });
                                } else {
                                    setFormData({
                                        ...formData,
                                        projectCode: '',
                                        projectName: ''
                                    });
                                }
                            }}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Project"
                                    required
                                    placeholder="Search and select a project"
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            fullWidth
                        />

                        <Autocomplete
                            options={departments}
                            value={selectedDepartment}
                            onChange={(_event, newValue) => {
                                setSelectedDepartment(newValue);
                                if (newValue) {
                                    setFormData({
                                        ...formData,
                                        departmentCode: newValue.code,
                                        departmentName: newValue.name
                                    });
                                } else {
                                    setFormData({
                                        ...formData,
                                        departmentCode: '',
                                        departmentName: ''
                                    });
                                }
                            }}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Department"
                                    placeholder="Search and select a department"
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            fullWidth
                        />

                        <Autocomplete
                            options={costCodes}
                            value={selectedCostCode}
                            onChange={(_event, newValue) => {
                                setSelectedCostCode(newValue);
                                if (newValue) {
                                    setFormData({
                                        ...formData,
                                        costCode: newValue.code,
                                        costCodeName: newValue.name
                                    });
                                } else {
                                    setFormData({
                                        ...formData,
                                        costCode: '',
                                        costCodeName: ''
                                    });
                                }
                            }}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Project Cost Code"
                                    placeholder="Search and select a cost code"
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            fullWidth
                        />

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Fiscal Year"
                                type="number"
                                value={formData.fiscalYear}
                                onChange={(e) => setFormData({ ...formData, fiscalYear: Number(e.target.value) })}
                                required
                                fullWidth
                            />
                            <FormControl fullWidth required>
                                <InputLabel>Month</InputLabel>
                                <Select
                                    value={formData.month}
                                    label="Month"
                                    onChange={(e) => setFormData({ ...formData, month: Number(e.target.value) })}
                                >
                                    {monthNames.map((name, index) => (
                                        <MenuItem key={index + 1} value={index + 1}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                label="Value in SAR"
                                type="number"
                                value={formData.valueSAR}
                                onChange={(e) => setFormData({ ...formData, valueSAR: Number(e.target.value) })}
                                required
                                fullWidth
                                InputProps={{
                                    inputProps: { min: 0, step: 0.01 }
                                }}
                            />
                            <TextField
                                label="Tolerance %"
                                type="number"
                                value={formData.tolerancePercent}
                                onChange={(e) => setFormData({ ...formData, tolerancePercent: Number(e.target.value) })}
                                fullWidth
                                InputProps={{
                                    inputProps: { min: 0, max: 100, step: 0.01 }
                                }}
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editingBudget ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BudgetNew;
